import AngleSmallDownSvg from "@icons/angle-small-down.svg?react";
import GarageOpenSvg from "@icons/garage-open.svg?react";
import { t } from "@lingui/core/macro";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Failed } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { PageTitle } from "@/common/components/page-title/page-title";
import {
  PriceItem,
  PriceList,
} from "@/common/components/price-list/price-list";
import {
  SearchField,
  useSearchFieldParams,
} from "@/common/components/search-field/search-field";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spinner } from "@/common/components/spinner/spinner";
import { WrapMeWith } from "@/common/components/wrap-me-with/wrap-me-with";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useIsMobile } from "@/common/hooks/use-media-query";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";

import { TwoColumnLayout } from "../layout/layout";
import { getPricesView } from "../purchase-prices/purchase-prices";
import { CommodityCard } from "../storages/components/commodity-card/commodity-card";
import { storagesQuery } from "./trades.api";

const path = "trades";

const Trades = () => {
  const { id: companyId } = useRequiredErpCompany();
  const { _ } = useLingui();
  const [search] = useSearchFieldParams();
  const { offerId } = useParams<"offerId">();
  const [CurrencySwitch, currency] = useCurrency();
  const storages = useQuery(
    storagesQuery({
      companyId,
      // @ts-expect-error discrepancy between CF API and IDSign API
      currency,
      search,
    }),
  );
  const isMobile = useIsMobile();
  const isDetailOpen = offerId !== undefined;
  const sidebar = useSidebar();

  return (
    <TwoColumnLayout
      left={{
        content: (
          <>
            <Metadata title={_(msg`Komodity k prodeji`)} />
            {storages.status === "pending" ? (
              <div className="flex basis-full items-center justify-center">
                <Spinner />
              </div>
            ) : null}
            {storages.status === "error" ? (
              <Failed error={storages.error} />
            ) : null}
            {storages.status === "success" ? (
              <>
                <SearchField placeholder={t`Hledat sklad nebo komoditu`} />
                <ul className="flex flex-col gap-y-6">
                  {storages.data.map((storage) => (
                    <li className="last-of-type:mb-8" key={storage.id}>
                      <details className="group" open={!isMobile}>
                        <summary className="flex list-none flex-col">
                          <h3 className="flex items-center gap-x-2 font-bold text-can-forest-teal [&_path]:fill-[currentColor]">
                            <GarageOpenSvg className="h-4 w-4" />
                            {storage.mark ? (
                              <mark className="text-can-slate-blue-gray">
                                {storage.name}
                              </mark>
                            ) : (
                              storage.name
                            )}
                            <AngleSmallDownSvg className="ml-auto rotate-180 group-open:rotate-0" />
                          </h3>
                        </summary>
                        <ul
                          className={`mt-6 grid grid-cols-1 gap-6  ${isDetailOpen || sidebar.state.screen ? "xl:grid-cols-2" : "md:grid-cols-2 2xl:grid-cols-3"}`}
                        >
                          {storage.offers.map((offer) => (
                            <li
                              className={
                                offerId === offer.id
                                  ? "[&_article]:bg-can-silver-gray [&_h4]:text-can-forest-teal [&_path]:fill-can-forest-teal"
                                  : ""
                              }
                              key={offer.id}
                            >
                              <CommodityCard>
                                <CommodityCard.Title
                                  crop={{
                                    harvestYear: offer.harvestYear,
                                    mark: offer.crop.mark,
                                    name: offer.crop.name,
                                  }}
                                />
                                <div className="flex flex-wrap gap-1">
                                  <CommodityCard.Stored tons={offer.amount} />
                                  |
                                  <CommodityCard.Price
                                    currency={currency}
                                    pricePerTon={offer.prices.at(0)?.value}
                                    tons={offer.amount}
                                  />
                                </div>
                                {getPricesView(offer.prices).map(
                                  (prices, i) => (
                                    <PriceList
                                      className={
                                        i === 0 && prices.length === 1
                                          ? "[&>li]:col-start-4 [&>li]:border-r-0 [&>li]:border-l"
                                          : ""
                                      }
                                      key={i}
                                      prices={prices.map((p) =>
                                        p?.type === "data"
                                          ? {
                                              currency,
                                              id: p.id,
                                              quarter: p.quarter,
                                              value: p.price,
                                            }
                                          : null,
                                      )}
                                      renderPrice={(price) =>
                                        price ? (
                                          <WrapMeWith
                                            when={!!price}
                                            wrapper={(children) => (
                                              <SearchParamsLink
                                                className="text-can-slate-blue-gray flex flex-col gap-y-2"
                                                to={`${storage.id}/offers/${offer.id}/quarter/${price.quarter.id}`}
                                              >
                                                {children}
                                              </SearchParamsLink>
                                            )}
                                          >
                                            <PriceItem price={price} />
                                          </WrapMeWith>
                                        ) : null
                                      }
                                    />
                                  ),
                                )}
                              </CommodityCard>
                            </li>
                          ))}
                        </ul>
                      </details>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ),
        header: (
          <>
            <PageTitle>
              <Trans>Komodity k prodeji</Trans>
            </PageTitle>
            {CurrencySwitch}
          </>
        ),
      }}
      right={isDetailOpen}
    />
  );
};

export { path, Trades };
